import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ordersApi, type OrderStatus } from '@/lib/api/arrex/orders';
import { settingsApi } from '@/lib/api/arrex/settings';
import { downloadOrderInvoice } from '@/lib/invoice';
import { useApp } from '@/contexts/AppContext';
import { formatPrice, localized, imgUrl } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Loader2, Search, X, Check, ChevronDown, Package, User, MapPin, Phone, Calendar as CalendarIcon, ExternalLink, ArrowDownWideNarrow, ArrowUpNarrowWide, Trash2, FileArchive, Pencil } from 'lucide-react';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DialogFooter } from '@/components/ui/dialog';
import { readItems } from '@/lib/api/normalize';

// Display order
const STATUS_ORDER = ['pending', 'confirmed', 'shipped', 'delivered', 'returned', 'cancelled'] as const;

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-orange-500/15 text-orange-500 border border-orange-500/50 dark:text-orange-400',
  confirmed: 'bg-primary/15 text-primary border border-primary/40',
  shipped: 'bg-success/15 text-success border border-success/40',
  delivered: 'bg-success text-success-foreground border border-success',
  returned: 'bg-amber-500/30 text-amber-900 border border-amber-700/60',
  cancelled: 'bg-destructive/15 text-destructive border border-destructive/40',
};

function StatusPicker({ value, onChange, t }: { value: string; onChange: (v: string) => void; t: (k: string) => string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex-1 min-w-[120px] h-7 px-2.5 rounded-md border border-border bg-background hover:border-primary/50 transition-colors flex items-center justify-between gap-2 text-xs"
        >
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize', STATUS_BADGE[value])}>
            {t(`admin.status.${value}`)}
          </span>
          <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1.5 w-48 rounded-lg" align="end">
        <div className="space-y-0.5">
          {STATUS_ORDER.map(s => {
            const selected = s === value;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { onChange(s); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors text-start',
                  selected ? 'bg-primary/15' : 'hover:bg-muted/60'
                )}
              >
                <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border', STATUS_BADGE[s])}>
                  {t(`admin.status.${s}`)}
                </span>
                {selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterPicker({ value, onChange, t }: { value: string; onChange: (v: string) => void; t: (k: string) => string }) {
  const [open, setOpen] = useState(false);
  const options = ['all', ...STATUS_ORDER];
  const label = value === 'all' ? t('admin.table.all') : t(`admin.status.${value}`);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full sm:w-56 h-9 px-3 rounded-md border border-border bg-background hover:border-primary/50 transition-colors flex items-center justify-between gap-2 text-sm"
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-muted-foreground text-xs shrink-0">Status:</span>
            {value === 'all' ? (
              <span className="font-medium capitalize truncate">{label}</span>
            ) : (
              <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border whitespace-nowrap', STATUS_BADGE[value])}>
                {label}
              </span>
            )}
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1.5 w-52 rounded-lg" align="end">
        <div className="space-y-0.5">
          {options.map(s => {
            const selected = s === value;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { onChange(s); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors text-start',
                  selected ? 'bg-primary/15' : 'hover:bg-muted/60'
                )}
              >
                {s === 'all' ? (
                  <span className="text-sm font-medium">All</span>
                ) : (
                  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border', STATUS_BADGE[s])}>
                    {t(`admin.status.${s}`)}
                  </span>
                )}
                {selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PAYMENT_STATUS_BADGE: Record<string, string> = {
  paid: 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/40 dark:text-emerald-400',
  unpaid: 'bg-amber-500/15 text-amber-600 border border-amber-500/40 dark:text-amber-400',
  pending: 'bg-blue-500/15 text-blue-600 border border-blue-500/40 dark:text-blue-400',
  failed: 'bg-destructive/15 text-destructive border border-destructive/40',
};

function PaymentStatusBadge({
  status,
  t,
  className,
}: {
  status?: string;
  t: (k: string, opts?: any) => string;
  className?: string;
}) {
  const norm = (status || 'unpaid').toLowerCase();
  const badgeCls = PAYMENT_STATUS_BADGE[norm] || PAYMENT_STATUS_BADGE.unpaid;
  const label = t(`admin.paymentStatus.${norm}`, { defaultValue: norm === 'paid' ? 'Paid' : 'Unpaid' });
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border inline-flex items-center gap-1 shrink-0', badgeCls, className)}>
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', norm === 'paid' ? 'bg-emerald-500' : 'bg-amber-500')} />
      {label}
    </span>
  );
}


function PaymentFilterPicker({ value, onChange, t }: { value: string; onChange: (v: string) => void; t: (k: string, opts?: any) => string }) {
  const [open, setOpen] = useState(false);
  const options = ['all', 'paid', 'unpaid'];
  const label = value === 'all' ? t('admin.paymentStatus.all', { defaultValue: 'All' }) : t(`admin.paymentStatus.${value}`, { defaultValue: value });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full sm:w-48 h-9 px-3 rounded-md border border-border bg-background hover:border-primary/50 transition-colors flex items-center justify-between gap-2 text-sm"
        >
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-muted-foreground text-xs shrink-0">{t('admin.ordersPage.paymentStatusLabel', { defaultValue: 'Payment:' })}</span>
            {value === 'all' ? (
              <span className="font-medium capitalize truncate">{label}</span>
            ) : (
              <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border whitespace-nowrap inline-flex items-center gap-1', PAYMENT_STATUS_BADGE[value])}>
                <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', value === 'paid' ? 'bg-emerald-500' : 'bg-amber-500')} />
                {label}
              </span>
            )}
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1.5 w-44 rounded-lg" align="end">
        <div className="space-y-0.5">
          {options.map(s => {
            const selected = s === value;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { onChange(s); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors text-start',
                  selected ? 'bg-primary/15' : 'hover:bg-muted/60'
                )}
              >
                {s === 'all' ? (
                  <span className="text-sm font-medium">{t('admin.paymentStatus.all', { defaultValue: 'All' })}</span>
                ) : (
                  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border inline-flex items-center gap-1', PAYMENT_STATUS_BADGE[s])}>
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', s === 'paid' ? 'bg-emerald-500' : 'bg-amber-500')} />
                    {t(`admin.paymentStatus.${s}`, { defaultValue: s })}
                  </span>
                )}
                {selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EditOrderModal({
  order, open, onOpenChange, onSaved, lang, t,
}: { order: any | null; open: boolean; onOpenChange: (v: boolean) => void; onSaved: () => void; lang: string; t: (k: string) => string }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [discount, setDiscount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setName(order.customer?.name || '');
      setPhone(order.customer?.phone || '');
      setAddress(order.address?.address || order.address?.street || '');
      setCity(order.address?.city || '');
      setDiscount(order.discount ? String(order.discount) : '0');
    }
  }, [order]);

  if (!order) return null;

  const subtotal = Number(order.subtotal || 0);
  const shipping = Number(order.shipping || 0);
  const discValue = Math.max(0, Number(discount) || 0);
  const total = Math.max(0, subtotal + shipping - discValue);

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Name required');
    if (!phone.trim()) return toast.error('Phone required');
    if (!address.trim()) return toast.error('Address required');
    if (!city.trim()) return toast.error('City required');

    setSaving(true);
    try {
      await ordersApi.update(order.id, {
        customer_name: name,
        phone,
        address,
        city,
        discount_amount: discValue,
      });
      toast.success('Order updated successfully');
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin.ordersPage.editOrder')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>{t('admin.ordersPage.customerName')}</Label>
            <Input value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('admin.ordersPage.phone')}</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('admin.ordersPage.city')}</Label>
              <Input value={city} onChange={e => setCity(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.ordersPage.discountEgp')}</Label>
              <Input type="number" min={0} value={discount} onChange={e => setDiscount(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('admin.ordersPage.address')}</Label>
            <Input value={address} onChange={e => setAddress(e.target.value)} />
          </div>

          <div className="rounded-lg border border-border p-3 space-y-1.5 text-xs bg-muted/20">
            <div className="flex justify-between"><span className="text-muted-foreground">{t('admin.common.subtotal')}</span><span>{formatPrice(subtotal, lang)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t('admin.common.shipping')}</span><span>{shipping > 0 ? formatPrice(shipping, lang) : t('admin.common.free')}</span></div>
            {discValue > 0 && (
              <div className="flex justify-between text-destructive font-medium">
                <span>{t('admin.common.discount')}</span>
                <span>-{formatPrice(discValue, lang)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-sm pt-1.5 border-t border-border"><span>{t('admin.common.newTotal')}</span><span className="text-primary">{formatPrice(total, lang)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('admin.common.cancel')}</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 me-1 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const RESTORE_STATUSES = new Set(['returned', 'cancelled']);

function OrderDetailsModal({
  order, open, onOpenChange, lang, t,
}: { order: any | null; open: boolean; onOpenChange: (v: boolean) => void; lang: string; t: (k: string) => string }) {
  // Items arrive pre-enriched from the API
  const details: any[] | null = order ? (order.items || []) : null;

  if (!order) return null;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{order.order_number || order.orderNumber || order.id}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border p-4 space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t('admin.ordersPage.orderInfo')}</h3>
              <div className="flex items-center gap-2 text-sm"><CalendarIcon className="w-4 h-4 text-muted-foreground" />{new Date(order.created_at).toLocaleString()}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={cn(STATUS_BADGE[order.status], 'capitalize px-2 py-0.5 rounded-full text-[10px] font-semibold')}>
                  {t(`admin.status.${order.status}`)}
                </Badge>
                <PaymentStatusBadge status={order.payment_status || order.paymentStatus} t={t} />
              </div>
              <div className="text-xs text-muted-foreground pt-0.5">
                {t('admin.ordersPage.paymentMethod', { defaultValue: 'Payment Method' })}: <span className="font-semibold text-foreground uppercase">{order.payment_method || order.paymentMethod || 'COD'}</span>
              </div>
            </div>
            <div className="rounded-lg border border-border p-4 space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t('admin.ordersPage.customer')}</h3>
              <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-muted-foreground" />{order.customer?.name}</div>
              <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-muted-foreground" />{order.customer?.phone}</div>
              <div className="flex items-start gap-2 text-sm"><MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" /><span className="break-words"><span className="font-medium">{order.address?.city}</span> — {order.address?.address || order.address?.street}</span></div>
              {order.delivery_time && (
                <div className="text-xs text-muted-foreground">Delivery: {order.delivery_time}</div>
              )}
            </div>

          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Items ({order.items?.length || 0})</h3>
            {!details ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="space-y-2">
                {details.map((it) => (
                  <Link
                    key={it.id}
                    to={it.product?.slug ? `/product/${it.product.slug}` : '#'}
                    target="_blank"
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/30 transition-colors"
                  >
                    <img src={imgUrl(it.image) || '/placeholder.svg'} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{it.name ? it.name : '—'}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        {it.color && (
                          <span className="flex items-center gap-1">
                            {it.color}
                          </span>
                        )}
                        {it.size && <span>· {it.size}</span>}
                        <span>· × {it.quantity}</span>
                      </div>
                    </div>
                    <div className="text-end shrink-0">
                      <div className="text-sm font-bold text-primary whitespace-nowrap">{formatPrice(Number(it.price) * it.quantity, lang)}</div>
                      <div className="text-[11px] text-muted-foreground">{formatPrice(Number(it.price), lang)} each</div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </Link>
                ))}

              </div>
            )}
          </div>

          <div className="rounded-lg border border-border p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t('admin.common.subtotal')}</span><span>{formatPrice(Number(order.subtotal), lang)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t('admin.common.shipping')}</span><span>{Number(order.shipping) > 0 ? formatPrice(Number(order.shipping), lang) : <span className="text-success">{t('admin.common.free')}</span>}</span></div>
            {order.discount && Number(order.discount) > 0 && (
              <div className="flex justify-between text-destructive font-medium">
                <span>{t('admin.common.discount')}</span>
                <span>-{formatPrice(Number(order.discount), lang)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-2 border-t border-border"><span>{t('admin.common.total')}</span><span className="text-primary">{formatPrice(Number(order.total), lang)}</span></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrders() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [orders, setOrders] = useState<any[] | null>(null);
  const [filter, setFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [generating, setGenerating] = useState<string | null>(null);
  const [activeOrder, setActiveOrder] = useState<any | null>(null);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletingAll, setDeletingAll] = useState(false);
  // Time/date filters (server-side, UTC)
  const [preset, setPreset] = useState<string>('all');
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editOrder, setEditOrder] = useState<any | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Build a server-side {from,to} range from the active preset / custom date.
  const dateRange = (): { from?: string; to?: string } => {
    if (customDate) {
      const start = new Date(customDate); start.setHours(0, 0, 0, 0);
      const end = new Date(customDate); end.setHours(23, 59, 59, 999);
      return { from: start.toISOString(), to: end.toISOString() };
    }
    if (preset === 'all') return {};
    const hoursMap: Record<string, number> = { '3h': 3, '6h': 6, '12h': 12, '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };
    const hours = hoursMap[preset];
    if (!hours) return {};
    return { from: new Date(Date.now() - hours * 3600 * 1000).toISOString() };
  };

  const currentFilters = () => ({ ...dateRange(), status: filter, q: debouncedSearch || undefined });

  const loadOrders = () => ordersApi.adminList(dateRange()).then((res) => setOrders(readItems(res)));

  useEffect(() => {
    setOrders(null);
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customDate]);

  const PRESETS: { key: string; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: '3h', label: '3h' },
    { key: '6h', label: '6h' },
    { key: '12h', label: '12h' },
    { key: '24h', label: '24h' },
    { key: '7d', label: '7d' },
    { key: '30d', label: '30d' },
  ];

  const exportAll = async () => {
    setExporting(true);
    try {
      const blob = await ordersApi.exportInvoices(currentFilters());
      downloadBlob(blob, `invoices-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success('Invoices exported');
    } catch (e: any) {
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };



  const sortByDate = (list: any[]) => {
    return [...list].sort((a, b) => {
      const ad = new Date(a.created_at).getTime();
      const bd = new Date(b.created_at).getTime();
      return sortDir === 'desc' ? bd - ad : ad - bd;
    });
  };

  const sortByStatus = (list: any[]) => {
    return [...list].sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a.status);
      const bi = STATUS_ORDER.indexOf(b.status);
      if (ai !== bi) return ai - bi;
      const ad = new Date(a.created_at).getTime();
      const bd = new Date(b.created_at).getTime();
      return sortDir === 'desc' ? bd - ad : ad - bd;
    });
  };

  const matchesSearch = (o: any) => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch;
    if (o.order_number?.toLowerCase().includes(q)) return true;
    if (o.phone?.toLowerCase().includes(q)) return true;
    if (o.customer_name?.toLowerCase().includes(q)) return true;
    if (o.order_items?.some((it: any) =>
      it.product?.name_en?.toLowerCase().includes(q) ||
      it.product?.name_ar?.toLowerCase().includes(q)
    )) return true;
    return false;
  };

  const matchesPayment = (o: any) => {
    if (paymentFilter === 'all') return true;
    const norm = (o.payment_status || o.paymentStatus || 'unpaid').toLowerCase();
    return norm === paymentFilter;
  };

  const baseList = orders ? (filter === 'all' ? sortByStatus(orders) : sortByDate(orders.filter(o => o.status === filter))) : [];
  const filtered = baseList.filter(matchesSearch).filter(matchesPayment);
  const normalizedDeleteConfirm = deleteConfirm.trim().toUpperCase();
  const canDeleteAll = normalizedDeleteConfirm === 'DELETE ALL' || deleteConfirm.trim() === 'حذف الكل';

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const generateInvoice = async (orderId: string, orderNumber?: string) => {
    setGenerating(orderId);
    try {
      const order = orders?.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');
      let settings: any = {};
      try { settings = (await settingsApi.adminGet()).settings || {}; } catch { /* sender info optional */ }
      await downloadOrderInvoice(order, settings);
      toast.success('Invoice downloaded');
    } catch (e: any) {
      toast.error(e.message || 'Invoice failed');
    } finally {
      setGenerating(null);
    }
  };

  const updateOrderStatus = async (id: string, status: string) => {
    const prev = orders?.find(o => o.id === id);
    const prevStatus = prev?.status;
    try {
      const res = await ordersApi.updateStatus(id, status as OrderStatus);
      setOrders(o => (o || []).map(x => x.id === id ? { ...x, status, invoice_url: res.invoice_url ?? x.invoice_url } : x));
      toast.success('Updated');

      // Server restores stock on transition into returned/cancelled.
      if (prev && RESTORE_STATUSES.has(status) && !RESTORE_STATUSES.has(prevStatus)) {
        toast.success(lang === 'ar' ? 'تمت إعادة المخزون' : 'Stock restored');
      }
    } catch (e: any) {
      toast.error(e.message || 'Update failed');
    }
  };


  const deleteAllOrders = async () => {
    setDeletingAll(true);
    try {
      if (!orders || orders.length === 0) { toast.info('No orders to delete'); return; }
      await ordersApi.deleteAll();
      await loadOrders();
      setDeleteAllOpen(false);
      setDeleteConfirm('');
      toast.success(lang === 'ar'
        ? 'تم حذف جميع الطلبات. سيبدأ الترقيم من SEEN-1001'
        : 'All orders deleted. Numbering will restart at SEEN-1001');
    } catch (e: any) {
      console.error('Delete all orders failed:', e);
      toast.error(e.message || 'Delete failed');
    } finally {
      setDeletingAll(false);
    }

  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold">{t('admin.orders.title')}</h1>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => { setDeleteConfirm(''); setDeleteAllOpen(true); }}
          disabled={!orders || orders.length === 0}
          className="h-9"
        >
          <Trash2 className="w-4 h-4 me-1.5" />
          {lang === 'ar' ? 'حذف جميع الطلبات' : 'Delete All Orders'}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.ordersPage.searchPlaceholder')}
            className="ps-9 pe-9 h-9 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
              aria-label={t('admin.ordersPage.clearSearch')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <FilterPicker value={filter} onChange={setFilter} t={t} />
        <PaymentFilterPicker value={paymentFilter} onChange={setPaymentFilter} t={t} />
        <button
          type="button"
          onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
          aria-label={sortDir === 'desc' ? 'Sort: Newest first' : 'Sort: Oldest first'}
          title={sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
          className="h-9 px-3 rounded-md border border-border bg-background hover:border-primary/50 transition-colors flex items-center justify-center gap-1.5 text-sm shrink-0"
        >
          {sortDir === 'desc'
            ? <ArrowDownWideNarrow className="w-4 h-4 text-primary" />
            : <ArrowUpNarrowWide className="w-4 h-4 text-primary" />}
          <span className="text-xs font-medium">{sortDir === 'desc' ? 'Newest' : 'Oldest'}</span>
        </button>
      </div>

      {/* Time / date filters + bulk export */}
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => { setCustomDate(undefined); setPreset(p.key); }}
            className={cn(
              'h-8 px-3 rounded-md border text-xs font-medium transition-colors',
              !customDate && preset === p.key
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background hover:border-primary/50'
            )}
          >
            {p.label}
          </button>
        ))}
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'h-8 px-3 rounded-md border text-xs font-medium flex items-center gap-1.5 transition-colors',
                customDate ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:border-primary/50'
              )}
            >
              <CalendarIcon className="w-3.5 h-3.5" />
              {customDate ? format(customDate, 'PP') : 'Pick a day'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={customDate}
              onSelect={(d) => { setCustomDate(d || undefined); setPreset('all'); setDatePickerOpen(false); }}
              initialFocus
              className={cn('p-3 pointer-events-auto')}
            />
          </PopoverContent>
        </Popover>
        {customDate && (
          <button
            type="button"
            onClick={() => setCustomDate(undefined)}
            className="h-8 px-2 rounded-md border border-border bg-background hover:bg-muted text-muted-foreground"
            aria-label={t('admin.ordersPage.clearDate')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 ms-auto"
          disabled={exporting || !orders || orders.length === 0}
          onClick={exportAll}
        >
          {exporting ? <Loader2 className="w-4 h-4 me-1.5 animate-spin" /> : <FileArchive className="w-4 h-4 me-1.5" />}
          {lang === 'ar' ? 'تنزيل كل الفواتير' : 'Download All PDFs'}
        </Button>
      </div>



      {!orders ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <>
          {/* Unified card layout for all viewports */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map(o => (
              <div
                key={o.id}
                role="button"
                tabIndex={0}
                onClick={() => setActiveOrder(o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveOrder(o); } }}
                className="surface-card rounded-lg p-3 sm:p-4 space-y-2 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] text-muted-foreground break-all">{o.order_number || o.orderNumber || o.id}</p>
                    <p className="font-medium text-sm break-words">{o.customer?.name}</p>
                    <p className="text-xs text-muted-foreground break-words">{o.customer?.phone} · {o.address?.city}</p>
                  </div>
                  <div className="text-end space-y-1 shrink-0">
                    <p className="font-bold text-primary text-sm whitespace-nowrap">{formatPrice(o.total, lang)}</p>
                    <div>
                      <PaymentStatusBadge status={o.payment_status || o.paymentStatus} t={t} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <Badge className={`${STATUS_BADGE[o.status] || ''} font-semibold capitalize px-2 py-0.5 rounded-full text-[10px]`}>
                    {t(`admin.status.${o.status}`)}
                  </Badge>
                  <StatusPicker value={o.status} onChange={(v) => updateOrderStatus(o.id, v)} t={t} />
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" disabled={generating === o.id} onClick={() => generateInvoice(o.id, o.id?.split('-')[0]?.toUpperCase())}>
                    {generating === o.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><Download className="w-3 h-3 me-1" />PDF</>}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={(e) => { e.stopPropagation(); setEditOrder(o); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">{t('admin.ordersPage.noOrders')}</p>}
          </div>
        </>
      )}

      <OrderDetailsModal
        order={activeOrder}
        open={!!activeOrder}
        onOpenChange={(v) => { if (!v) setActiveOrder(null); }}
        lang={lang}
        t={t}
      />

      <EditOrderModal
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(v) => { if (!v) setEditOrder(null); }}
        onSaved={loadOrders}
        lang={lang}
        t={t}
      />

      <AlertDialog open={deleteAllOpen} onOpenChange={(v) => { setDeleteAllOpen(v); if (!v) setDeleteConfirm(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {lang === 'ar' ? 'حذف جميع الطلبات' : 'Delete All Orders'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === 'ar'
                ? 'هل أنت متأكد من حذف جميع الطلبات؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to delete ALL orders? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {lang === 'ar'
                ? 'اكتب "DELETE ALL" أو "حذف الكل" للتأكيد:'
                : 'Type "DELETE ALL" to confirm:'}
            </p>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE ALL"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!canDeleteAll || deletingAll}
              onClick={(e) => { e.preventDefault(); deleteAllOrders(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingAll
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (lang === 'ar' ? 'حذف الكل' : 'Delete All')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
